import React, { ReactNode } from 'react'
import classNames from 'classnames'
import { TextInputIconType } from 'components/atoms/TextInput'
import { Dictionary, sortBy } from 'lodash'
import { matchSorter } from 'match-sorter'
import {
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  createFilterOptions,
  FilterOptionsState,
} from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import { brandColors } from '../../../theme'
import { IAutocompleteOption } from '../../Autocomplete'
import { TIconName, ICONS_BY_NAME } from '../../Autocomplete/utils'
import { Avatar } from '../../Avatar'
import { IAutoCompleteGroupObject } from '../types'
import { EMPTY_AUTOCOMPLETE_VALUE, UNASSIGNED_VALUE } from './utils'

/**
 * Removes options with certain IDs
 */
export function filterOutExtraOptions<T extends IAutocompleteOption>(opts: T[] = []): T[] {
  return opts.filter(({ id }: T) => {
    return ![EMPTY_AUTOCOMPLETE_VALUE.id, UNASSIGNED_VALUE.id].includes(id)
  })
}

interface IIconProps {
  iconName: TIconName
  className?: string

  /** @default true */
  fullWidth?: boolean
}

/**
 * Get icon JSX by passing name
 */
export const OptionIcon: React.FC<IIconProps> = ({ className, fullWidth = true, iconName }) => {
  if (!iconName || !ICONS_BY_NAME[iconName]) return <span className='option-icon-invalid' />
  const IconComponent = ICONS_BY_NAME[iconName]
  const classes = classNames(fullWidth ? 'autocomplete-option-icon' : '', className)
  return <IconComponent data-test='autocomplete-option-icon' className={classes} />
}

interface InputIconCondition {
  when: (val: string) => boolean
  iconName: keyof typeof TextInputIconType
}

/**
 * Returns the type of icon based on the value of the input
 * and a series of ordered conditions that you can also include
 *
 * @param {keyof typeof TextInputIconType} fallbackIconName - Icon name
 * @param {string} inputValue - String value of input
 * @param {InputIconCondition[]} conditions - The array of conditions that can be acceptable args
 * @example const icon = getInputIcon({ conditions: [{ when: (val) => val.includes('location'), iconName: 'locationPinIcon' }]})
 */
export const getInputIcon = (
  fallbackIconName: keyof typeof TextInputIconType = 'search',
  inputValue: string,
  conditions: InputIconCondition[] = [
    {
      when: (val: string = '') => val?.toLowerCase().includes('add '),
      iconName: 'plus',
    },
  ]
): keyof typeof TextInputIconType => {
  for (const { iconName, when: conditionFunc } of conditions) {
    if (conditionFunc && conditionFunc(inputValue)) return iconName
    break
  }
  return fallbackIconName
}

export type RenderGroupType = 'default' | 'withoutGroupName'
export type RenderGroupFunc = ({ children, group, key }: AutocompleteRenderGroupParams) => ReactNode

/**
 * Returns custom rendered group for <Autosuggest />
 * @param {RenderGroupType} type - Optional param. Returns default if left blank
 * @param {Dictionary<IAutoCompleteGroupObject>} optionGroupsByGroupIndex
 */
export const getRenderGroupProps = (
  type: RenderGroupType,
  optionGroupsByGroupIndex: Dictionary<IAutoCompleteGroupObject>
): RenderGroupFunc => {
  const RENDER_GROUPS: Record<RenderGroupType, RenderGroupFunc> = {
    default: ({ children, group, key }: AutocompleteRenderGroupParams) => {
      const selectedGroup: IAutoCompleteGroupObject = optionGroupsByGroupIndex[group]
      const { className, groupName } = selectedGroup || {}
      const classes = classNames('autocomplete-group', className)

      return (
        <div key={key} className={classes} role='group' aria-label={group}>
          {groupName && <p className='autocomplete-group-label'>{groupName}</p>}
          {children}
        </div>
      )
    },
    /**
     * Returns a group without the `groupName`
     */
    withoutGroupName: ({ children, group, key }: AutocompleteRenderGroupParams) => {
      const selectedGroup: IAutoCompleteGroupObject = optionGroupsByGroupIndex[group]
      const { className } = selectedGroup || {}
      const classes = classNames('autocomplete-group', className)

      return (
        <div key={key} className={classes} role='group' aria-label={group}>
          {children}
        </div>
      )
    },
  }
  if (!type || !RENDER_GROUPS[type]) return RENDER_GROUPS.default
  return RENDER_GROUPS[type]
}

export type RenderOptionType =
  | 'default'
  | 'withAvatar'
  | 'withIcon'
  | 'withEmail'
  | 'withIconAndDescription'

export type RenderOptionFunc<T> = (
  option: T,
  state: AutocompleteRenderOptionState,

  // Added to accomodate `roger` UI pattern of highlighting
  // query text with bold style
  markup?: ReactNode
) => ReactNode

/**
 * Gets custom-rendered option for <Autosuggest />
 * @param {RenderOptionType} [type] - Optional param
 * @param {function} [getOptionLabel] - Returns a label used on each option
 */
export function getRenderOptionProps(
  type?: RenderOptionType,
  getOptionLabel?: (option: IAutocompleteOption) => string
): RenderOptionFunc<IAutocompleteOption> | undefined {
  const RENDER_OPTIONS: Record<string, RenderOptionFunc<IAutocompleteOption> | undefined> = {
    default: undefined,
    /**
     * Render option with an Avatar or User icon, depending on value
     */
    withAvatar: (
      option: IAutocompleteOption,
      state: AutocompleteRenderOptionState,
      markup?: ReactNode
    ) => {
      if (!getOptionLabel) return markup
      const text = getOptionLabel(option)
      return (
        <div className='flex autocomplete-option-avatar'>
          <div className='flex mr2'>
            {text?.includes('Add ') ? (
              <OptionIcon iconName='PlusIcon' fullWidth={false} />
            ) : (
              <Avatar key={option.id} name={text} size='input' hideTooltip />
            )}
          </div>
          <div className='flex-auto tl'>{markup}</div>
        </div>
      )
    },
    withIcon: (
      option: IAutocompleteOption,
      state: AutocompleteRenderOptionState,
      markup?: ReactNode
    ) => {
      if (!getOptionLabel) return markup
      const { iconName } = option || {}

      return (
        <div className='autocomplete-option-with-icon'>
          {iconName && (
            <div className='autocomplete-option-with-icon-element'>
              <span className='autocomplete-option-with-icon-element-inner'>
                <OptionIcon iconName={iconName} />
              </span>
            </div>
          )}
          <div className='autocomplete-option-with-icon-text'>{markup}</div>
        </div>
      )
    },
    /**
     * With extra description information that doesn't get highlighted
     * with input value
     */
    withIconAndDescription: (
      option: IAutocompleteOption,
      state: AutocompleteRenderOptionState,
      markup?: ReactNode
    ) => {
      if (!getOptionLabel) return markup
      const { description = '', iconName } = option || {}

      return (
        <div className='autocomplete-option-with-icon'>
          {iconName && (
            <div className='autocomplete-option-with-icon-element'>
              <span className='autocomplete-option-with-icon-element-inner'>
                <OptionIcon iconName={iconName} />
              </span>
            </div>
          )}
          <div className='autocomplete-option-with-icon-text'>
            <Box display='flex'>
              <Box flexGrow={0} flexShrink={1} flexBasis='auto'>
                {markup}
              </Box>
              <Box flexGrow={1} flexShrink={1} flexBasis='auto' pl={1}>
                <Box component='span' color={brandColors.coolGray5}>
                  {description}
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      )
    },
    /**
     * Custom option for showing email and name
     */
    withEmail: (
      option: IAutocompleteOption,
      state: AutocompleteRenderOptionState,
      markup?: ReactNode
    ) => {
      if (!getOptionLabel) return markup
      const { email, iconName, text: name = '' } = option || {}
      return (
        <div className='autocomplete-option-with-icon'>
          {iconName && (
            <div className='autocomplete-option-with-icon-element'>
              <span className='autocomplete-option-with-icon-element-inner'>
                <OptionIcon iconName={iconName} />
              </span>
            </div>
          )}
          <div className='autocomplete-option-with-icon-text'>
            {name && <span className='b'>{name}</span>} {email && <span>{email}</span>}
          </div>
        </div>
      )
    },
  }
  if (!type || !RENDER_OPTIONS[type]) return RENDER_OPTIONS.default
  return RENDER_OPTIONS[type]
}

export type StartAdornmentType = 'default' | 'withAvatar'
export type IStartAdornmentFunc<T> = (currentInputValue: T | null) => ReactNode

/**
 * Gets custom rendered startAdornment prop for <Autosuggest />
 * @param {StartAdornmentType} [type] - Optional argument. If empty, returns default
 */
export const getStartAdornmentProps = (
  type?: StartAdornmentType
): IStartAdornmentFunc<IAutocompleteOption> | undefined => {
  const ADORNMENTS: Record<
    StartAdornmentType,
    IStartAdornmentFunc<IAutocompleteOption> | undefined
  > = {
    default: undefined,
    withAvatar: (currentInputValue: IAutocompleteOption | null) => {
      const { text } = currentInputValue || {}

      let avatar = (
        <span className='avatar-wrapper'>
          <Avatar name={text} size='input' hideTooltip />
        </span>
      )

      if (text?.toLowerCase().includes('add ')) {
        avatar = <OptionIcon iconName='PlusIcon' className='input-icon input-icon-green' />
      }

      return avatar
    },
  }
  if (!type || !ADORNMENTS[type]) return ADORNMENTS.default
  return ADORNMENTS[type]
}

export type FilterOptionType =
  | 'default'
  | 'filter'
  | 'limitOptions'
  | 'fuzzy'
  | 'fuzzyGroup'
  | 'showAll'
export type FilterOptionFunc<T> = (options: T[], state: FilterOptionsState<T>) => T[]

/**
 * Get custom filter prop for <Autosuggest />
 * @param {FilterOptionType} [type] - optional type
 */
export function getFilterOptionProps(
  type?: FilterOptionType
): FilterOptionFunc<IAutocompleteOption> | undefined {
  const FILTER_OPTIONS: Record<string, FilterOptionFunc<IAutocompleteOption> | undefined> = {
    default: undefined,
    filter: (opts: IAutocompleteOption[], params: FilterOptionsState<IAutocompleteOption>) => {
      const options = filterOutExtraOptions(opts)
      const filter = createFilterOptions<IAutocompleteOption>()
      return filter(options, params)
    },
    /**
     * Filter results using fuzzy search.
     * @see https://material-ui.com/components/autocomplete/#advanced
     */
    fuzzy: (
      opts: IAutocompleteOption[] = [],
      { inputValue = '' }: FilterOptionsState<IAutocompleteOption>
    ) => {
      const options = filterOutExtraOptions(opts)
      return matchSorter(options, inputValue, { keys: ['text'] })
    },
    /**
     * Filter results using fuzzy search. Also adds `groupIndex` as priority
     * _after_ fuzzy search sorting.
     * @note Useful for sorting results with multiple option groups
     * @see https://material-ui.com/components/autocomplete/#advanced
     */
    fuzzyGroup: (
      opts: IAutocompleteOption[] = [],
      { inputValue = '' }: FilterOptionsState<IAutocompleteOption>
    ) => {
      const options = filterOutExtraOptions(opts)
      const matchedOptions = matchSorter(options, inputValue, { keys: ['text'] })
      return sortBy(matchedOptions, 'groupIndex')
    },
    limitOptions: (
      opts: IAutocompleteOption[] = [],
      params: FilterOptionsState<IAutocompleteOption>
    ) => {
      const options = filterOutExtraOptions(opts)
      const filter = createFilterOptions<IAutocompleteOption>({
        limit: 10,
      })
      return filter(options, params)
    },
    showAll: opts => filterOutExtraOptions(opts),
  }
  if (!type || !FILTER_OPTIONS[type]) return FILTER_OPTIONS.default
  return FILTER_OPTIONS[type]
}
